import React, { useState, useEffect } from 'react';
import { 
    ListChecks, FileText, Link as LinkIcon, CalendarDays, ClipboardCheck, Settings, 
    Database, Building2, Building, Server, Table, Wand2, Download, ChevronDown, 
    ChevronRight, Tag, Users, Layers, Shield, FileSearch, Calendar, Briefcase,
    Menu, X, CheckSquare, MapPin, TrendingUp, DollarSign, Zap, BarChart3, HelpCircle, Trash2
} from 'lucide-react';
import { NavLink, Outlet, useLocation, useParams, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { getAppBranding } from '../config/portalMode';

const MainLayout = ({ 
    user, 
    role, 
    hasJobListAccess, 
    hasSetupAccess, 
    isAdmin,
    canViewWorkflows = false,
    dealerships = [],
    onLogout
}) => {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [expandedMenus, setExpandedMenus] = useState(new Set(['setup']));
    const location = useLocation();
    const branding = getAppBranding();
    const { dealershipId } = useParams();
    const navigate = useNavigate();

    // Auto-expand Setup menu if on any setup/admin route
    useEffect(() => {
        const setupRoutes = ['/admin/workflows', '/admin/message-suites', '/admin/message-templates', 
                            '/admin/brand-templates', '/admin/dealership-onboarding', '/admin/imported-contact-plans',
                            '/admin/workflow-templates', '/admin/workflow-builder', '/setup'];
        const isSetupRoute = setupRoutes.some(route => location.pathname.startsWith(route));
        if (isSetupRoute && !expandedMenus.has('setup')) {
            setExpandedMenus(prev => new Set([...prev, 'setup']));
        }
    }, [location.pathname, expandedMenus]);
    
    // Get current dealership if on a dealership page
    const currentDealership = dealershipId ? dealerships.find(d => d.id === dealershipId) : null;

    const NavItem = ({ to, icon: Icon, label, onClick }) => {
        const Component = onClick ? 'button' : NavLink;
        const props = onClick 
            ? { onClick, className: `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 w-full text-left` }
            : {
                to,
                className: ({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                        isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                    }`
            };
        
        return (
            <Component {...props}>
                <Icon size={18} />
                {!sidebarCollapsed && <span>{label}</span>}
            </Component>
        );
    };

    const SubNavItem = ({ to, icon: Icon, label }) => (
        <NavLink
            to={to}
            onClick={(e) => {
                // Ensure clicks on sub-items work properly and aren't blocked by DndContext
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
            }}
            onMouseDown={(e) => {
                // Prevent DndContext from capturing mouse events
                e.stopPropagation();
            }}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 ml-6 rounded-md text-sm font-medium ${
                    isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                }`
            }
            style={{ position: 'relative', zIndex: 20 }}
        >
            <Icon size={16} />
            {!sidebarCollapsed && <span>{label}</span>}
        </NavLink>
    );

    const NestedExpandableNavItem = ({ id, label, children }) => {
        const isExpanded = expandedMenus.has(id);
        const hasActiveChild = children?.some(child => child.to && location.pathname.startsWith(child.to));

        const toggleExpansion = () => {
            const newExpanded = new Set(expandedMenus);
            if (isExpanded) {
                newExpanded.delete(id);
            } else {
                newExpanded.add(id);
            }
            setExpandedMenus(newExpanded);
        };

        if (sidebarCollapsed) {
            return null; // Don't show nested groups when sidebar is collapsed
        }

        return (
            <div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleExpansion();
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 ml-6 rounded-md text-sm font-medium ${
                        hasActiveChild ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <span className="flex-1 text-left">{label}</span>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {isExpanded && children && (
                    <div className="mt-1 space-y-1" onClick={(e) => e.stopPropagation()}>
                        {children.map((child, index) => (
                            <NavLink
                                key={index}
                                to={child.to}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.nativeEvent.stopImmediatePropagation();
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                }}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2 ml-12 rounded-md text-sm font-medium ${
                                        isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                                    }`
                                }
                                style={{ position: 'relative', zIndex: 20 }}
                            >
                                <child.icon size={16} />
                                {!sidebarCollapsed && <span>{child.label}</span>}
                            </NavLink>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const ExpandableNavItem = ({ id, icon: Icon, label, children, groups }) => {
        const isExpanded = expandedMenus.has(id);
        const hasActiveChild = children?.some(child => child.to && location.pathname.startsWith(child.to)) ||
                              groups?.some(group => group.children?.some(child => child.to && location.pathname.startsWith(child.to)));

        const toggleExpansion = () => {
            const newExpanded = new Set(expandedMenus);
            if (isExpanded) {
                newExpanded.delete(id);
            } else {
                newExpanded.add(id);
            }
            setExpandedMenus(newExpanded);
        };

        if (sidebarCollapsed) {
            return (
                <div className="relative group">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleExpansion();
                        }}
                        className={`w-full flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${
                            hasActiveChild ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title={label}
                    >
                        <Icon size={18} />
                    </button>
                    {isExpanded && (
                        <div 
                            className="absolute left-full ml-2 top-0 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[200px]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {children?.map((child, index) => (
                                <NavLink
                                    key={index}
                                    to={child.to}
                                    onClick={(e) => e.stopPropagation()}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-3 py-2 text-sm font-medium ${
                                            isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                                        }`
                                    }
                                >
                                    <child.icon size={16} />
                                    <span>{child.label}</span>
                                </NavLink>
                            ))}
                            {groups?.map((group, groupIndex) => (
                                <div key={groupIndex} className="border-t border-gray-200 mt-1 pt-1">
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                                        {group.label}
                                    </div>
                                    {group.children?.map((child, childIndex) => (
                                        <NavLink
                                            key={childIndex}
                                            to={child.to}
                                            onClick={(e) => e.stopPropagation()}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 px-3 py-2 text-sm font-medium ${
                                                    isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                                                }`
                                            }
                                        >
                                            <child.icon size={16} />
                                            <span>{child.label}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleExpansion();
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                        hasActiveChild ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <Icon size={18} />
                    <span className="flex-1 text-left">{label}</span>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {isExpanded && (
                    <div className="mt-1 space-y-1" onClick={(e) => e.stopPropagation()}>
                        {/* Render direct children (non-grouped items) */}
                        {children?.map((child, index) => (
                            <SubNavItem key={index} {...child} />
                        ))}
                        {/* Render grouped items */}
                        {groups?.map((group, groupIndex) => (
                            <NestedExpandableNavItem 
                                key={groupIndex}
                                id={`${id}-${group.id}`}
                                label={group.label}
                                children={group.children}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-screen overflow-hidden">
            {/* Top Header Bar */}
            <header className="bg-white shadow-sm border-b flex items-center justify-between px-6 py-3 z-10">
                <div className="flex items-center gap-3">
                    <Logo size={36} />
                    <h1 className="text-base font-bold text-blue-600">{branding.name}</h1>
                </div>
                {user && (
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/help')}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Help & Documentation"
                        >
                            <HelpCircle size={18} />
                            {!sidebarCollapsed && <span>Help</span>}
                        </button>
                        <span className="text-sm text-gray-600">
                            {user.first_name && user.last_name 
                                ? `${user.first_name} ${user.last_name}` 
                                : user.email}
                        </span>
                        {!sidebarCollapsed && (
                            <span className="text-sm text-gray-500">
                                Role: <strong className="capitalize">{role}</strong>
                            </span>
                        )}
                        {onLogout && (
                            <button 
                                onClick={onLogout} 
                                className="px-4 py-2 text-sm bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                Logout
                            </button>
                        )}
                    </div>
                )}
            </header>

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <aside 
                    className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r transition-all duration-300 flex flex-col`}
                    style={{ zIndex: 100, position: 'relative' }}
                    onClick={(e) => {
                        // Ensure sidebar clicks don't get captured by DndContext
                        e.stopPropagation();
                        e.nativeEvent?.stopImmediatePropagation();
                    }}
                    onMouseDown={(e) => {
                        // Prevent DndContext from capturing mouse events in sidebar
                        e.stopPropagation();
                    }}
                >
                    {/* Sidebar Header with hamburger */}
                    <div className="p-4 border-b flex items-center justify-between">
                        {!sidebarCollapsed && (
                            <h2 className="text-lg font-semibold text-gray-700">Navigation</h2>
                        )}
                        <button
                            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                            className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
                            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {sidebarCollapsed ? <Menu size={20} /> : <X size={20} />}
                        </button>
                    </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto p-4 space-y-2">
                    {/* Setup Section - MOVED TO TOP */}
                    {(hasSetupAccess || isAdmin) && (
                        <div className="mb-4">
                            {!sidebarCollapsed && (
                                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 px-3">Setup</h3>
                            )}
                            <ExpandableNavItem
                                id="setup"
                                icon={Building2}
                                label="Setup"
                                groups={[
                                    {
                                        id: "dealership-config",
                                        label: "Dealership Configuration",
                                        children: [
                                            { to: "/setup", icon: Building2, label: "Dealership Management" },
                                            { to: "/setup/oems", icon: Building2, label: "OEMs" },
                                            { to: "/admin/dealership-onboarding", icon: Building2, label: "Dealership Onboarding" }
                                        ]
                                    },
                                    {
                                        id: "templates-content",
                                        label: "Templates & Content",
                                        children: [
                                            ...(canViewWorkflows ? [{ to: "/admin/workflows", icon: Zap, label: "Workflows" }] : []),
                                            { to: "/admin/message-suites", icon: FileText, label: "Message Suites" },
                                            { to: "/admin/message-templates", icon: FileText, label: "Message Templates" },
                                            { to: "/admin/brand-templates", icon: Tag, label: "Brand Templates" }
                                        ]
                                    },
                                    {
                                        id: "data-management",
                                        label: "Data Management",
                                        children: [
                                            { to: "/admin/imported-contact-plans", icon: Zap, label: "Imported Contact Plans" }
                                        ]
                                    }
                                ]}
                            />
                            
                            {/* Show dealership sub-pages if viewing a specific dealership */}
                            {currentDealership && !sidebarCollapsed && (
                                <div className="ml-4 mt-2 space-y-1 border-l-2 border-gray-200 pl-2">
                                    <div className="px-3 py-1 text-xs font-medium text-gray-700 truncate" title={currentDealership.name || currentDealership.group_name || 'Dealership'}>
                                        {currentDealership.name || currentDealership.group_name || 'Dealership'}
                                    </div>
                                    <SubNavItem 
                                        to={`/setup/${dealershipId}`} 
                                        icon={Briefcase} 
                                        label="Dashboard" 
                                    />
                                    <SubNavItem 
                                        to={`/setup/${dealershipId}/checklist`} 
                                        icon={CheckSquare} 
                                        label="Checklist" 
                                    />
                                    <SubNavItem 
                                        to={`/setup/${dealershipId}/locations`} 
                                        icon={MapPin} 
                                        label="Locations" 
                                    />
                                    <SubNavItem 
                                        to={`/setup/${dealershipId}/signoff`} 
                                        icon={ClipboardCheck} 
                                        label="Sign Off" 
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Job List & Invoicing - MOVED AFTER SETUP */}
                    {(hasJobListAccess || isAdmin) && (
                        <div className="mb-4">
                            <ExpandableNavItem
                                id="joblist"
                                icon={Briefcase}
                                label="Job List & Invoicing"
                                children={[
                                    { to: "/joblist/dashboard", icon: Briefcase, label: "Dashboard" },
                                    { to: "/joblist/clients", icon: Building, label: "Clients" },
                                    { to: "/joblist/jobs", icon: Briefcase, label: "Jobs" },
                                    { to: "/joblist/invoices", icon: FileText, label: "Invoices" },
                                    { to: "/joblist/pipeline", icon: TrendingUp, label: "Pipeline" },
                                    { to: "/joblist/rate-card", icon: DollarSign, label: "Rate Card" }
                                ]}
                            />
                        </div>
                    )}

                    {/* Event Management Section - internal and client portal users */}
                    {(hasSetupAccess || isAdmin || role === 'client') && (
                        <div className="mb-4">
                            {role === 'client' ? (
                                <div className="space-y-1">
                                    <NavItem to="/events" icon={Calendar} label="Events" />
                                    <NavItem to="/events/appointments" icon={CalendarDays} label="Appointments" />
                                    <NavItem to="/events/reports" icon={BarChart3} label="View Reports" />
                                    <NavItem to="/events/calendar-integrations" icon={Calendar} label="Calendar Sync" />
                                    <NavItem to="/events/security" icon={Shield} label="Security" />
                                </div>
                            ) : (
                                <ExpandableNavItem
                                    id="event-management"
                                    icon={Calendar}
                                    label="Event Management"
                                    children={[
                                        { to: "/events", icon: Calendar, label: "Events" },
                                        { to: "/events/appointments", icon: CalendarDays, label: "Appointments" },
                                        { to: "/events/reports", icon: BarChart3, label: "View Reports" },
                                        { to: "/events/calendar-integrations", icon: Calendar, label: "Calendar Sync" },
                                    ]}
                                />
                            )}
                        </div>
                    )}

                    {/* Admin Section */}
                    {isAdmin && (
                        <div className="mb-4">
                            <ExpandableNavItem
                                id="administration"
                                icon={Settings}
                                label="Administration"
                                groups={[
                                    {
                                        id: "field-mapping",
                                        label: "Field Mapping",
                                        children: [
                                            { to: "/admin/checklist", icon: ListChecks, label: "Checklist" },
                                            { to: "/admin/factfind", icon: FileText, label: "Fact Find" },
                                            { to: "/admin/links", icon: LinkIcon, label: "Links" },
                                            { to: "/admin/signoff", icon: ClipboardCheck, label: "Sign Off" }
                                        ]
                                    },
                                    {
                                        id: "reference-data",
                                        label: "Reference Data",
                                        children: [
                                            { to: "/admin/brands", icon: Tag, label: "Brands" },
                                            { to: "/admin/oems", icon: Building2, label: "OEMs" },
                                            { to: "/admin/departments", icon: Building, label: "Departments" },
                                            { to: "/admin/groups", icon: Building2, label: "Dealership Groups" }
                                        ]
                                    },
                                    {
                                        id: "user-management",
                                        label: "User Management",
                                        children: [
                                            { to: "/admin/users", icon: Users, label: "Internal users" },
                                            { to: "/admin/client-portal-users", icon: Users, label: "Client Portal users" },
                                            { to: "/admin/roles", icon: Shield, label: "Roles & Permissions" }
                                        ]
                                    },
                                    {
                                        id: "data-operations",
                                        label: "Data Operations",
                                        children: [
                                            { to: "/admin/data-imports", icon: Database, label: "Data Imports" },
                                            { to: "/admin/data-exports", icon: Download, label: "Data Exports" },
                                            { to: "/admin/database-config", icon: Server, label: "Database Config" },
                                            { to: "/admin/table-browser", icon: Table, label: "Table Browser" }
                                        ]
                                    },
                                    {
                                        id: "integrations",
                                        label: "Integrations",
                                        children: [
                                            { to: "/admin/field-integration", icon: Wand2, label: "Field Integration" },
                                            { to: "/admin/voicebox-sync", icon: Database, label: "VoiceBox Sync" },
                                            { to: "/admin/xero-config", icon: Settings, label: "Xero Config" }
                                        ]
                                    },
                                    {
                                        id: "system",
                                        label: "System",
                                        children: [
                                            { to: "/admin/settings", icon: Settings, label: "General Settings" },
                                            { to: "/admin/cleanup", icon: Trash2, label: "Data Cleanup" },
                                            { to: "/admin/audit-logs", icon: FileSearch, label: "Audit Logs" },
                                            { to: "/admin/financial-years", icon: Calendar, label: "Financial Years" }
                                        ]
                                    }
                                ]}
                            />
                        </div>
                    )}
                </nav>
            </aside>

                {/* Main Content */}
                <main className="flex-1 overflow-y-auto bg-gray-50 p-8">
                    <Outlet context={{ user, role }} />
                </main>
            </div>
        </div>
    );
};

export default MainLayout;

